import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';

// #region Utils
/**
 * Check if the current window is inside an iframe.
 *
 * @returns {boolean} True if in an iframe, otherwise false.
 */
function checkIsInMsalIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}
/**
 * Check if the current window is a popup.
 *
 * @returns {boolean} True if in a popup, otherwise false.
 */
function checkIsInMsalPopup(): boolean {
  return window.opener != null;
}
/**
 * Determine if the current call is a silent MSAL call.
 *
 * @returns {boolean} True if it's a silent call, otherwise false.
 */
function checkMsalSilentCall(): boolean {
  return checkIsInMsalIframe();
}
/**
 * Determine if the current call is a popup MSAL call.
 *
 * @returns {boolean} True if it's a popup call, otherwise false.
 */
function checkMsalPopupCall(): boolean {
  return checkIsInMsalPopup();
}
/**
 * Determine if the current call is a redirect MSAL call.
 *
 * @param {Location} location - The location object to check.
 * @returns {boolean} True if it's a redirect call, otherwise false.
 */
function checkMsalRedirectCall(location: Pick<Location, 'hash'>): boolean {
  return (location.hash?.length ?? 0) > 0;
}
/**
 * Determine if navigation should be prevented based on MSAL call type.
 *
 * @param {Location} location - The location object to check.
 * @returns {boolean} True if navigation should be prevented, otherwise false.
 */
function shouldPreventNavigationForMsal(
  location: Pick<Location, 'hash'> = window.location,
): boolean {
  return checkMsalSilentCall() || checkMsalPopupCall() || checkMsalRedirectCall(location);
}
// #endregion

export default function useIsMsalProcessing() {
  const location = useLocation();

  return useMemo(() => shouldPreventNavigationForMsal(location), [location]);
}
